/*
 * Proprietary and confidential | 2026 | SoundTalks NV
 */

import { Map as ImmutableMap } from 'immutable';
import { denormalize } from 'normalizr';
import * as schemas from 'data/utils/schemas';
import {
  itemsPagination,
  listLoadingProgress,
  loadingProgress,
  createLoadingProgress,
  updateLoadingProgress,
  versionsListLoadingProgress,
  versionCreateLoadingProgress,
} from 'data/reducers/admin/alert-rules';

import createImmutableEqualSelector from '../../utils/immutable-selector';

const getState = (state: any) => state.adminAlertRules;

export const getEntities = (state: any) => getState(state).getIn(['entities', 'alertRules']);
export const getVersionEntities = (state: any) => getState(state).getIn(['entities', 'alertRuleVersions']);

export const getItem = (state: any, id: string | number) =>
  getEntities(state).get(String(id)) || getEntities(state).get(id);
export const getCurrentRuleId = (state: any) => getState(state).get('currentRuleId');

export const getCurrentItem = createImmutableEqualSelector(
  getEntities,
  getVersionEntities,
  getCurrentRuleId,
  (alertRules, alertRuleVersions, currentRuleId) =>
    currentRuleId
      ? denormalize(currentRuleId, schemas.alertRule, ImmutableMap({ alertRules, alertRuleVersions }))
      : null,
);

export const getItemsList = createImmutableEqualSelector(
  getEntities,
  getVersionEntities,
  getState,
  (alertRules, alertRuleVersions, state) =>
    denormalize(
      itemsPagination.getPageItems(state),
      [schemas.alertRule],
      ImmutableMap({ alertRules, alertRuleVersions }),
    ),
);

export const getItemsTotal = (state: any) => itemsPagination.getTotalItems(getState(state));
export const getItemsPerPage = (state: any) => itemsPagination.getItemsPerPage(getState(state));

export const getItemsListLoaded = (state: any) => listLoadingProgress.getLoaded(getState(state));
export const getItemsListLoading = (state: any) => listLoadingProgress.getLoading(getState(state));
export const getItemsListLoadFailed = (state: any) => listLoadingProgress.getLoadFailed(getState(state));

export const getItemLoaded = (state: any) => loadingProgress.getLoaded(getState(state));
export const getItemLoading = (state: any) => loadingProgress.getLoading(getState(state));
export const getItemLoadFailed = (state: any) => loadingProgress.getLoadFailed(getState(state));

export const getCreateLoaded = (state: any) => createLoadingProgress.getLoaded(getState(state));
export const getCreateLoading = (state: any) => createLoadingProgress.getLoading(getState(state));
export const getCreateLoadFailed = (state: any) => createLoadingProgress.getLoadFailed(getState(state));

export const getUpdateLoaded = (state: any) => updateLoadingProgress.getLoaded(getState(state));
export const getUpdateLoading = (state: any) => updateLoadingProgress.getLoading(getState(state));
export const getUpdateLoadFailed = (state: any) => updateLoadingProgress.getLoadFailed(getState(state));

export const getVersionsForRule = createImmutableEqualSelector(
  getVersionEntities,
  (state: any, ruleId: string | number) => getState(state).getIn(['versionsByRuleId', String(ruleId)]),
  (alertRuleVersions, versionIds) =>
    versionIds ? denormalize(versionIds, [schemas.alertRuleVersion], ImmutableMap({ alertRuleVersions })) : null,
);

export const getVersionsListLoaded = (state: any) => versionsListLoadingProgress.getLoaded(getState(state));
export const getVersionsListLoading = (state: any) => versionsListLoadingProgress.getLoading(getState(state));
export const getVersionsListLoadFailed = (state: any) => versionsListLoadingProgress.getLoadFailed(getState(state));

export const getVersionCreateLoaded = (state: any) => versionCreateLoadingProgress.getLoaded(getState(state));
export const getVersionCreateLoading = (state: any) => versionCreateLoadingProgress.getLoading(getState(state));
export const getVersionCreateLoadFailed = (state: any) => versionCreateLoadingProgress.getLoadFailed(getState(state));
